import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import { roleKeys, type RoleKey } from '@/features/auth/domain/auth'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { EmployeeRepository } from '../../application/EmployeeRepository'
import { commandErrorCode, EmployeeCommandError, type EmployeeListItem } from '../../domain/employee'

const knownRoles = new Set<string>(roleKeys)

const rankedRoutesSchema = z.object({
  employeeId: z.string(),
  routes: z.array(
    z.object({
      rank: z.number().int().positive(),
      id: z.string(),
      name: z.string(),
      color: z.string().nullable(),
      season: z.string(),
      available: z.boolean(),
    }),
  ),
})

const employeeSchema = z.object({
  userId: z.string().nullable(),
  employeeId: z.string().nullable(),
  prenom: z.string().nullable(),
  nom: z.string().nullable(),
  displayName: z.string().nullable(),
  identifier: z.string().nullable(),
  identityLocked: z.boolean(),
  identityToComplete: z.boolean(),
  roles: z
    .array(z.string())
    .transform((values) => values.filter((value): value is RoleKey => knownRoles.has(value))),
  status: z.enum(['ACTIVE', 'DISABLED', 'NO_ACCOUNT']),
  mustChangePassword: z.boolean(),
  lastLoginAt: z.string().nullable(),
  route: z
    .object({
      id: z.string(),
      name: z.string(),
      color: z.string().nullable(),
      season: z.string().nullable(),
      available: z.boolean(),
    })
    .nullable(),
  equipment: z
    .object({
      id: z.string(),
      number: z.string().nullable(),
      name: z.string(),
      status: z.string(),
      active: z.boolean(),
      available: z.boolean(),
    })
    .nullable(),
})

const equipmentSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  name: z.string(),
  category: z.string().nullable(),
  status: z.string(),
  active: z.boolean(),
  available: z.boolean(),
})

const routeSchema = z.object({ id: z.string(), name: z.string(), color: z.string().nullable() })

const availabilitySchema = z.object({
  identifier: z.string(),
  available: z.boolean(),
  suggestion: z.string().nullable(),
})

const historySchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  type: z.string(),
  details: z.record(z.string(), z.unknown()),
  actorName: z.string().nullable(),
})

const accessLogSchema = z.object({
  total: z.number(),
  items: z.array(
    z.object({
      id: z.string(),
      occurredAt: z.string(),
      userId: z.string().nullable(),
      identifier: z.string().nullable(),
      accountName: z.string().nullable(),
      eventType: z.enum([
        'LOGIN_SUCCEEDED',
        'LOGIN_FAILED',
        'LOGOUT',
        'PASSWORD_CHANGED',
        'PASSWORD_TEMP_REPLACED',
      ]),
      success: z.boolean(),
      userAgent: z.string().nullable(),
      source: z.enum(['APPLICATION', 'LOGIN_SCREEN']),
    }),
  ),
})

const matrixSchema = z.object({
  roles: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      description: z.string(),
      permissions: z.array(z.string()),
      memberCount: z.number(),
    }),
  ),
  permissions: z.array(z.object({ key: z.string(), description: z.string() })),
  protected: z.array(z.string()),
})

const createResultSchema = z.object({
  status: z.literal('COMPLETED'),
  identifier: z.string(),
  employeeId: z.string().nullable().optional(),
})

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

/** Corps d'erreur `{ error: 'CODE' }` renvoyé par la fonction Edge. */
async function functionErrorCode(error: unknown): Promise<string> {
  const context = (error as { context?: unknown } | null)?.context
  if (context instanceof Response) {
    const body = (await context.json().catch(() => null)) as { error?: unknown } | null
    if (typeof body?.error === 'string') return body.error
  }
  return 'UNEXPECTED'
}

export function createSupabaseEmployeeRepository(client: AppSupabaseClient): EmployeeRepository {
  const rpcClient = client as unknown as SupabaseClient

  async function call(name: string, args?: Record<string, unknown>, signal?: AbortSignal): Promise<unknown> {
    const builder = rpcClient.rpc(name, args)
    const response = (await (signal ? builder.abortSignal(signal) : builder)) as unknown as RpcResponse
    if (response.error) throw new EmployeeCommandError(commandErrorCode(response.error.message))
    return response.data
  }

  async function invoke(body: Record<string, unknown>): Promise<unknown> {
    const invocation = await client.functions.invoke('employee-accounts', { body })
    if (invocation.error) throw new EmployeeCommandError(await functionErrorCode(invocation.error))
    return invocation.data as unknown
  }

  return {
    async list() {
      const rows = z.array(employeeSchema).parse(await call('list_employees_v1'))
      return rows.map((row): EmployeeListItem => ({
        ...row,
        key: row.userId ?? row.employeeId ?? '',
        displayName: row.displayName ?? row.identifier ?? 'Sans nom',
      }))
    },
    async listEquipments() {
      return z.array(equipmentSchema).parse(await call('list_equipments_v1'))
    },
    async listAssignableRoutes() {
      return z.array(routeSchema).parse(await call('list_assignable_routes_v1'))
    },
    async checkIdentifier(prenom, nom, signal) {
      return availabilitySchema.parse(
        await call('check_employee_identifier_v1', { p_prenom: prenom, p_nom: nom }, signal),
      )
    },
    async create(command) {
      const result = createResultSchema.parse(await invoke({ action: 'create', ...command }))
      return { identifier: result.identifier, employeeId: result.employeeId ?? null }
    },
    async setRole(userId, roleKey) {
      await call('set_employee_role_v1', { p_user_id: userId, p_role_key: roleKey })
    },
    async setActive(userId, active) {
      await call('set_user_active_v1', { p_user_id: userId, p_active: active })
    },
    async resetPassword(userId, temporaryPassword) {
      await invoke({ action: 'reset-password', userId, temporaryPassword })
    },
    async revokeSessions(userId) {
      await call('revoke_user_sessions_v1', { p_user_id: userId })
    },
    async completeIdentity(userId, prenom, nom) {
      await call('complete_employee_identity_v1', { p_user_id: userId, p_prenom: prenom, p_nom: nom })
    },
    async setAssignment(employeeId, kind, resourceId) {
      await call('set_employee_assignment_v1', { p_command: { employeeId, kind, resourceId } })
    },
    async listEmployeeRoutes() {
      return z.array(rankedRoutesSchema).parse(await call('employee_routes_v1'))
    },
    async setEmployeeRoutes(employeeId, routeIds) {
      return rankedRoutesSchema.parse(
        await call('set_employee_routes_v1', { p_command: { employeeId, routeIds } }),
      )
    },
    async history(userId, employeeId) {
      return z
        .array(historySchema)
        .parse(await call('employee_history_v1', { p_user_id: userId, p_employee_id: employeeId }))
    },
    async accessLog(filters) {
      return accessLogSchema.parse(await call('list_access_log_v1', { p_filters: filters }))
    },
    async rolesMatrix() {
      const matrix = matrixSchema.parse(await call('list_roles_permissions_v1'))
      return { roles: matrix.roles, permissions: matrix.permissions, protectedPermissions: matrix.protected }
    },
    async setRolePermission(roleKey, permissionKey, granted) {
      await call('set_role_permission_v1', {
        p_role_key: roleKey,
        p_permission_key: permissionKey,
        p_granted: granted,
      })
    },
    async createEquipment(input) {
      await call('create_equipment_v1', { p_command: input })
    },
    async setEquipmentActive(equipmentId, active) {
      await call('set_equipment_active_v1', { p_equipment_id: equipmentId, p_active: active })
    },
  }
}
